import cloneDeep from 'lodash/cloneDeep';
import { useEffect, useRef, useState } from 'react';
import { IFramework, useMoveEventListener } from '@/framework';
import { getCenterPoint, rotateImage } from '@/plugins/crop-img/new-components/utils';
import { ITouchKitEvent } from '@/utils/event-kit';
import { getPointsBetweenAngle, twoPointToVector } from '@/utils/math-kit';
import { BaseLayerViewType, IImageLayerProps, ILayerProps, IPoint, LayerType } from '@/version-types';

export const useRotateImageOperateRealTime = (activeLayer: ILayerProps, framework: IFramework) => {
    const originLayerRef = useRef<ILayerProps | undefined>(undefined);
    const lastPointRef = useRef<IPoint>({ x: 0, y: 0 });
    const originScaleRef = useRef<number>(1);
    const lastRotateRef = useRef<number>(0);
    const originLayer = useRef<ILayerProps | undefined>();
    // 上一次和初始角度的角度差
    const lastSubRotateRef = useRef<number>(0);
    const centerPointRef = useRef<IPoint | null>(null);
    const oldLayerRef = useRef<ILayerProps | null>(null);

    const [movePoint, setMovePoint] = useState<IPoint | null>(null);
    const [lastLayer, setLastLayer] = useState<ILayerProps | null>(framework.operate.getActivePickLayer());

    useEffect(() => {
        setLastLayer(activeLayer);
    }, [activeLayer]);

    useMoveEventListener((e: ITouchKitEvent) => {
        const layer = framework.operate.getActivePickLayer();
        if (!layer || layer?.layerType !== LayerType.IMAGE_LAYER) {
            return null;
        }

        if (e.isStart) {
            originLayerRef.current = cloneDeep(layer as ILayerProps);
            lastPointRef.current = { x: e.startPoint.x, y: e.startPoint.y };
            originScaleRef.current = (originLayerRef.current.data as IImageLayerProps).imageTransform.scale;
            lastRotateRef.current = (originLayerRef.current.data as IImageLayerProps).imageTransform.rotate;
            oldLayerRef.current = cloneDeep(layer as ILayerProps);
            originLayer.current = cloneDeep(layer);
            return;
        }

        // 渲染层不存在，不可操作
        if (!originLayerRef.current || originLayerRef.current?.layerType !== LayerType.IMAGE_LAYER) {
            return null;
        }

        const target = e.target;
        const dataType = target.getAttribute('data-operate-type');

        if (dataType !== 'imgRotate') return;

        const parentElement = target.parentElement;
        if (!parentElement) return;

        const { data } = originLayerRef.current;
        const { imageTransform } = data as IImageLayerProps;

        if (!centerPointRef.current) {
            centerPointRef.current = getCenterPoint(parentElement);
        }

        const currentPoint = { x: e.currentPoint.x, y: e.currentPoint.y };
        setMovePoint(currentPoint);
        // 获取从初始点的旋转角度
        let currentRotate = getPointsBetweenAngle(
            twoPointToVector(centerPointRef.current, lastPointRef.current),
            twoPointToVector(centerPointRef.current, currentPoint),
        );

        if (currentRotate < 0) {
            currentRotate = 360 + currentRotate;
        }

        const newLayer = rotateImage(
            originLayerRef.current,
            (imageTransform.rotate + currentRotate) % 360,
            originScaleRef.current,
        );
        lastSubRotateRef.current = currentRotate;
        lastRotateRef.current = (newLayer.data as IImageLayerProps).imageTransform.rotate;
        (originLayerRef.current.data as IImageLayerProps).imageTransform.scale = (
            newLayer.data as IImageLayerProps
        ).imageTransform.scale;
        framework.stage.addOrUpdate(cloneDeep(newLayer));

        if (e.isEnd && oldLayerRef.current) {
            framework.history.write(oldLayerRef.current, newLayer);
        }
        framework.operate.setActiveLayer(newLayer.id);
        setLastLayer(newLayer);
        framework.events.emitBaseOperate(newLayer.id, BaseLayerViewType.IMAGE_ROTATE, {
            isStart: e.isStart,
            isEnd: e.isEnd,
            oldLayer: originLayer.current,
        });
        framework.operate.setIsBaseOperate(true);
        if (e.isEnd) {
            framework.operate.setIsBaseOperate(false);
            originLayer.current = undefined;
        }
    });

    return {
        rotateMovePoint: movePoint,
        layer: lastLayer,
    };
};
